import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import {
  AfterPaymentError,
  NativeStoreError,
  PromotionalOfferSignatureError,
  StaleTransactionError,
  SubscriptionConflictError,
} from './errors'

/** A stage of a purchase or a free-trial claim (GLOSSARY.md, Purchase step). */
export type PurchaseStep =
  | 'auth'
  | 'read_service_level'
  | 'sign_promotional_offer'
  | 'create_order'
  | 'native_purchase'
  | 'recover_interrupted_purchase'
  | 'mark_processing'
  | 'post_transaction'

/** A restore's own stages, kept apart from a purchase's (ADR 0037). */
export type RestoreStep =
  | 'restore_auth'
  | 'restore_find_transaction'
  | 'restore_find_order'
  | 'restore_mark_processing'
  | 'restore_post_transaction'

/** A stage of loading a page (GLOSSARY.md, Page-load step). */
export type PageLoadStep =
  | 'load_user_info'
  | 'load_products'
  | 'load_store_products'
  | 'load_service_level'
  | 'load_trial_eligibility'
  | 'load_admission'
  | 'load_config'
  | 'load_activity'
  | 'load_progresses'
  | 'load_campaign_products'
  | 'load_coin_box_product'
  | 'load_gem_box_product'
  | 'load_coin_purchase_stats'
  | 'load_gem_purchase_stats'
  | 'load_challenges'
  | 'load_balance'
  | 'load_item_types'

/** Where a failure is placed when the failing step is not known. */
export type FallbackStep = 'purchase' | 'restore' | 'load'

export type FailureStep =
  | PurchaseStep
  | RestoreStep
  | PageLoadStep
  | FallbackStep

/** What an error dialog shows under its description (ADR 0037). */
export interface Failure {
  /** The failure code, `<step>:<cause>`. */
  code: string
  /** The Service API purchase ID (the Order ID), once the order exists. */
  purchaseId?: number
  /**
   * The store transaction ID, only when payment was taken but the
   * subscription was not linked.
   */
  transactionId?: string
}

export function failureCode(step: FailureStep, cause: string): string {
  return `${step}:${cause}`
}

/** Where a thrown error failed, recorded on it by `tagFailure`. */
export interface FailureTag {
  step: FailureStep
  cause: string
  purchaseId?: number
  transactionId?: string
}

const FAILURE_TAG = Symbol.for('@forest/paywall-core/failure')

/**
 * Records on `error` the step it failed at, so whoever shows its dialog can
 * name it. The cause is read now, before anything (step telemetry renames the
 * error) can change what it looks like. The first tag wins: it is the
 * innermost step. Returns `error`, for `throw tagFailure(error, step)`.
 */
export function tagFailure<E>(
  error: E,
  step: FailureStep,
  details: Partial<Omit<FailureTag, 'step'>> = {},
): E {
  if (typeof error === 'object' && error !== null && !(FAILURE_TAG in error)) {
    const tag: FailureTag = {
      step,
      cause: details.cause ?? failureCause(error),
      ...(details.purchaseId !== undefined && {
        purchaseId: details.purchaseId,
      }),
      ...(details.transactionId !== undefined && {
        transactionId: details.transactionId,
      }),
    }
    Object.defineProperty(error, FAILURE_TAG, { value: tag })
  }
  return error
}

/** The tag `tagFailure` recorded, or the fallback step with the error's cause. */
export function readFailureTag(
  error: unknown,
  fallbackStep: FallbackStep,
): FailureTag {
  if (typeof error === 'object' && error !== null && FAILURE_TAG in error) {
    return error[FAILURE_TAG] as FailureTag
  }
  return { step: fallbackStep, cause: failureCause(error) }
}

/**
 * The failure a dialog shows for `error`: its tagged step, or `fallbackStep`
 * when nothing tagged it, with the IDs the tag recorded.
 */
export function failureOf(error: unknown, fallbackStep: FallbackStep): Failure {
  const { step, cause, purchaseId, transactionId } = readFailureTag(
    error,
    fallbackStep,
  )
  return {
    code: failureCode(step, cause),
    ...(purchaseId !== undefined && { purchaseId }),
    ...(transactionId !== undefined && { transactionId }),
  }
}

/**
 * Whether `error` never reached the server or did not answer in time — what a
 * connection issue dialog is for. Decided by the failure's cause, so the
 * dialog never disagrees with the code it shows.
 */
export function isConnectionFailure(error: unknown): boolean {
  const { cause } = readFailureTag(error, 'load')
  return cause === 'network' || cause === 'timeout'
}

/**
 * `description` with the failure's lines under it: the code, followed by
 * ` #<purchase ID>`, then the transaction ID on a line of its own. A
 * description that already shows the code (`codeInDescription`) gains only the
 * IDs.
 */
export function withFailure(
  description: string,
  failure: Failure,
  options: { codeInDescription?: boolean } = {},
): string {
  const firstLine = [
    options.codeInDescription ? undefined : failure.code,
    failure.purchaseId === undefined ? undefined : `#${failure.purchaseId}`,
  ]
    .filter(Boolean)
    .join(' ')
  return [description, firstLine, failure.transactionId]
    .filter(Boolean)
    .join('\n')
}

/**
 * An error whose cause is known where it is thrown — data a step found
 * missing (`sku_missing`), or a native call's error — so the failure code can
 * name it instead of `unexpected`.
 */
export class FailureCauseError extends Error {
  readonly failureCause: string
  constructor(failureCause: string, message: string) {
    super(message)
    this.name = 'FailureCauseError'
    this.failureCause = failureCause
  }
}

/** The cause for a native call's `{ success: false, error }`. */
export function nativeErrorCause(error: string): string {
  return snakeCase(error)
}

/**
 * The cause half of a failure code (ADR 0037): what went wrong, never a raw
 * error message.
 */
export function failureCause(error: unknown): string {
  if (error instanceof FailureCauseError) return error.failureCause
  if (error instanceof NativeStoreError) return snakeCase(error.message)
  if (
    error instanceof PromotionalOfferSignatureError ||
    error instanceof AfterPaymentError ||
    error instanceof SubscriptionConflictError ||
    error instanceof StaleTransactionError
  ) {
    return failureCause(error.cause)
  }
  if (isHTTPError(error)) return `http_${error.response.status}`
  if (isTimeoutError(error)) return 'timeout'
  if (
    error instanceof Error &&
    (isNetworkError(error) || isWebKitLoadFailed(error))
  ) {
    return 'network'
  }
  return bridgeCause(error) ?? 'unexpected'
}

/**
 * WebKit's failed `fetch()`, optionally suffixed with a hostname.
 * `is-network-error` matches it only without a stack, but in the iOS WKWebView
 * it often carries one.
 */
function isWebKitLoadFailed(error: Error): boolean {
  return (
    error instanceof TypeError &&
    (error.message === 'Load failed' ||
      (error.message.startsWith('Load failed (') &&
        error.message.endsWith(')')))
  )
}

/**
 * bifrost-web's call errors, matched by name: paywall-core does not depend on
 * bifrost-web, and native-services' wrappers reject with them as they are.
 */
const BRIDGE_ERROR_NAMES = new Set([
  'DeliveryUnknownError',
  'TransportNotAdmittedError',
  'OverloadedError',
  'CallDeadlineExceededError',
  'CallCancelledError',
  'PayloadTooLargeError',
  'SessionReplacedError',
])
const BRIDGE_TIMEOUT_MESSAGES = new Set([
  'RPC call timeout',
  'Connection timeout',
])

function bridgeCause(error: unknown): string | undefined {
  if (!(error instanceof Error)) return undefined
  if (error.name === 'RpcCallError' && 'code' in error) {
    return `bridge_${String(error.code)}`
  }
  if (BRIDGE_ERROR_NAMES.has(error.name)) {
    return `bridge_${snakeCase(error.name.replace(/Error$/, ''))}`
  }
  if (BRIDGE_TIMEOUT_MESSAGES.has(error.message)) return 'bridge_timeout'
  return undefined
}

/** `product not found` → `product_not_found`, `CallCancelled` → `call_cancelled`. */
function snakeCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
}
